/**
 * ConferenciaListTable — Main table for conferência list.
 */
import React, { useMemo } from 'react';
import { MantineReactTable, useMantineReactTable, type MRT_ColumnDef } from 'mantine-react-table';
import { Badge, Tooltip, ActionIcon } from '@mantine/core';
import { ClipboardCheck, Eye, FileText, FileSpreadsheet } from 'lucide-react';
import { ConferenciaVerificacao, downloadConferenciaColetaPdf, downloadConferenciaColetaExcel } from '../../services/conferenciaService';

interface Props {
  conferencias: ConferenciaVerificacao[];
  onConferir: (row: ConferenciaVerificacao) => void;
}

export const ConferenciaListTable: React.FC<Props> = ({ conferencias, onConferir }) => {
  const columns = useMemo<MRT_ColumnDef<ConferenciaVerificacao>[]>(() => [
    {
      accessorKey: 'numero_conferencia',
      header: 'Nº Conf.',
      size: 90,
      Cell: ({ cell }) => {
        const val = cell.getValue<number | null>();
        return val ? <span className="font-mono font-bold">{val}</span> : <span className="text-gray-400">—</span>;
      },
    },
    {
      accessorKey: 'job_id',
      header: 'Contagem',
      size: 100,
      Cell: ({ cell, row }) => {
        const jobId = cell.getValue<number | null>();
        return jobId ? (
          <span className="font-mono text-sm">{jobId}</span>
        ) : (
          <span className="text-gray-400">—</span>
        );
      },
    },
    {
      accessorKey: 'cliente_nome',
      header: 'Cliente',
      size: 180,
      Cell: ({ cell }) => cell.getValue<string>() || <span className="text-gray-400">—</span>,
    },
    {
      accessorKey: 'hospital_nome',
      header: 'Hospital',
      size: 180,
      Cell: ({ cell }) => cell.getValue<string>() || <span className="text-gray-400">—</span>,
    },
    {
      accessorKey: 'ordem_coleta',
      header: 'O.C.',
      size: 100,
      Cell: ({ cell }) => cell.getValue<string>() || <span className="text-gray-400">—</span>,
    },
    {
      accessorKey: 'lacres',
      header: 'Lacres',
      size: 150,
      Cell: ({ cell }) => {
        const val = cell.getValue<string | null>();
        if (!val) return <span className="text-gray-400">—</span>;
        const items = val.split(',').map(l => l.trim()).filter(Boolean);
        return (
          <div className="flex flex-wrap gap-1">
            {items.slice(0, 3).map((l, i) => (
              <Badge key={i} size="xs" variant="outline" color="blue">{l}</Badge>
            ))}
            {items.length > 3 && (
              <Badge size="xs" variant="light" color="gray">+{items.length - 3}</Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'avulsa',
      header: 'Avulsa',
      size: 70,
      Cell: ({ cell }) => cell.getValue<boolean>() ? (
        <Badge size="xs" color="orange">S</Badge>
      ) : (
        <Badge size="xs" color="gray" variant="outline">N</Badge>
      ),
    },
    {
      accessorKey: 'finalizada',
      header: 'Conferido',
      size: 90,
      Cell: ({ row }) => {
        const { finalizada, total_itens, total_conferidos } = row.original;
        if (finalizada) {
          return <Badge size="sm" color="green">Finalizada</Badge>;
        }
        if (total_itens > 0 && total_conferidos > 0) {
          return (
            <Badge size="sm" color="yellow" variant="light">
              {total_conferidos}/{total_itens}
            </Badge>
          );
        }
        return <Badge size="sm" color="red" variant="light">Pendente</Badge>;
      },
    },
    {
      accessorKey: 'data_conferencia',
      header: 'Data',
      size: 120,
      Cell: ({ cell }) => cell.getValue<string>() || <span className="text-gray-400">—</span>,
    },
    {
      id: 'actions',
      header: 'Ação',
      size: 120,
      Cell: ({ row }) => (
        <div className="flex gap-1">
          <Tooltip label={row.original.finalizada ? 'Ver Conferência' : 'Conferir'}>
            <ActionIcon
              variant="light"
              color={row.original.finalizada ? 'gray' : 'blue'}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onConferir(row.original);
              }}
            >
              {row.original.finalizada ? <Eye size={16} /> : <ClipboardCheck size={16} />}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Gerar PDF Conferência de Coleta">
            <ActionIcon
              variant="light"
              color="orange"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                downloadConferenciaColetaPdf(row.original.id);
              }}
            >
              <FileText size={16} />
            </ActionIcon>
          </Tooltip>
          {/* N167: mesmo relatório em Excel. Em planilha dá para somar
              "A Coletar" x "Coletado" e filtrar as linhas com diferença --
              no PDF não dá. Verde para separar do PDF (laranja) na mesma linha. */}
          <Tooltip label="Gerar Excel Conferência de Coleta">
            <ActionIcon
              variant="light"
              color="green"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                downloadConferenciaColetaExcel(row.original.id);
              }}
            >
              <FileSpreadsheet size={16} />
            </ActionIcon>
          </Tooltip>
        </div>
      ),
    },
  ], [onConferir]);

  const table = useMantineReactTable({
    columns,
    data: conferencias,
    enableColumnFilters: false,
    enableSorting: true,
    enablePagination: true,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      density: 'xs',
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => onConferir(row.original),
      sx: { cursor: 'pointer' },
    }),
    mantineTableProps: {
      striped: true,
      highlightOnHover: true,
    },
  });

  return <MantineReactTable table={table} />;
};
